import React from 'react'
import {
    Modal,
    ModalBody,
    ModalHeader,
    ModalFooter,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
} from '@chakra-ui/react'
import type {
    ModalProps,
    ModalBodyProps,
    ModalHeaderProps,
    ModalFooterProps,
    ModalContentProps,
    ModalOverlayProps,
} from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/react'

import AlertChanges from '../alert-changes'

interface Props extends ModalProps {
    title?: string
    children: React.ReactNode
    onCancel?: () => void
    cancelText?: string
    onConfirm?: (() => void) | (() => Promise<void>)
    confirmText?: string
    isLoading?: boolean
    isConfirmDisabled?: boolean
    showCloseButton?: boolean
    warnBeforeClose?: boolean
    skipCloseOnConfirm?: boolean
    contentOverrides?: ModalContentProps
    headerOverrides?: ModalHeaderProps
    bodyOverrides?: ModalBodyProps
    footerOverrides?: ModalFooterProps
    overlayOverrides?: ModalOverlayProps
}

export default function SharedModal({
    title,
    children,
    onCancel,
    cancelText,
    onConfirm,
    confirmText,
    isLoading,
    isConfirmDisabled,
    showCloseButton = true,
    warnBeforeClose,
    skipCloseOnConfirm,
    contentOverrides,
    headerOverrides,
    bodyOverrides,
    footerOverrides,
    overlayOverrides,
    ...rest
}: Props) {
    const { onClose } = rest

    const {
        isOpen: isAlertOpen,
        onOpen: onAlertOpen,
        onClose: onAlertClose,
    } = useDisclosure()

    function handleClose() {
        if (warnBeforeClose) {
            onAlertOpen()
        } else {
            onCancel && onCancel()
            onClose()
        }
    }

    function handleConfirm() {
        onConfirm && onConfirm()
        !skipCloseOnConfirm && onClose()
    }

    return (
        <>
            <Modal size="2xl" {...rest} onClose={handleClose}>
                <ModalOverlay {...overlayOverrides} />
                <ModalContent {...contentOverrides}>
                    <ModalHeader {...headerOverrides}>{title}</ModalHeader>
                    {showCloseButton && <ModalCloseButton rounded="full" />}
                    <ModalBody {...bodyOverrides}>{children}</ModalBody>
                    {(onConfirm || onCancel) && (
                        <ModalFooter
                            justifyContent="space-between"
                            {...footerOverrides}
                        >
                            {onCancel && (
                                <Button
                                    colorScheme="gray"
                                    onClick={handleClose}
                                    disabled={isLoading}
                                    size="sm"
                                    rounded="lg"
                                >
                                    {cancelText || 'Cancel'}
                                </Button>
                            )}
                            {onConfirm && (
                                <Button
                                    colorScheme="brand"
                                    onClick={handleConfirm}
                                    isLoading={isLoading}
                                    isDisabled={isConfirmDisabled}
                                    size="sm"
                                    rounded="lg"
                                >
                                    {confirmText || 'Confirm'}
                                </Button>
                            )}
                        </ModalFooter>
                    )}
                </ModalContent>
            </Modal>
            {warnBeforeClose && (
                <AlertChanges
                    isOpen={isAlertOpen}
                    onClose={onAlertClose}
                    onDiscard={() => {
                        onCancel && onCancel()
                        onClose()
                    }}
                />
            )}
        </>
    )
}
